import { cn } from "@/utils/cn";
import formatCurrency from "@/utils/formatCurrency";
import formatTemplateString from "@/utils/formatTemplateString";
import { DashboardService } from "../service.dashboard";
import type { TSwitchTodayAmounts } from "../types.dashboard";
import { formatHeadlineAmount } from "../utils/formatHeadlineAmount";
import UpgradePromoCode, {
  type UpgradePromoCodeProps,
} from "./UpgradePromoCode";

const COPY = DashboardService.Content.yearlyPlanSaleLedger;

interface SummaryRowProps {
  label: string;
  value: string;
  className?: string;
}

const SummaryRow = ({ label, value, className }: SummaryRowProps) => (
  <div className={cn("flex items-center justify-between gap-4", className)}>
    <dt>{label}</dt>
    <dd className="tabular-nums">{value}</dd>
  </div>
);

export interface UpgradeSummaryProps extends TSwitchTodayAmounts {
  currency: string;
  yearlyPrice: number;
  /** Promo code field, shown between the breakdown and the total */
  promo: Omit<UpgradePromoCodeProps, "isZeroAmount">;
  className?: string;
}

const UpgradeSummary = ({
  currency,
  yearlyPrice,
  proratedCredit,
  discount,
  aftershootCredit,
  amountDueToday,
  promo,
  className,
}: UpgradeSummaryProps) => {
  const formatLedgerAmount = (amount: number) =>
    formatCurrency(amount, currency);

  const formatDeduction = (amount: number) =>
    formatTemplateString(
      COPY.switchToday.deduction,
      formatLedgerAmount(amount),
    );

  return (
    <div
      className={cn(
        "flex w-full flex-col gap-4 rounded-xl border border-edge-card bg-surface-bone p-3 font-archivo text-sm leading-[23.8px] font-light tracking-[0.14px] text-type-black",
        className,
      )}
    >
      <dl className="flex flex-col gap-4">
        <SummaryRow
          label={COPY.switchToday.annualPlan}
          value={formatLedgerAmount(yearlyPrice)}
        />
        {proratedCredit > 0 && (
          <SummaryRow
            label={COPY.switchToday.credited}
            value={formatDeduction(proratedCredit)}
          />
        )}
        {discount > 0 && (
          <SummaryRow
            label={COPY.switchToday.discount}
            value={formatDeduction(discount)}
          />
        )}
        {aftershootCredit > 0 && (
          <SummaryRow
            label={COPY.switchToday.aftershootCredit}
            value={formatDeduction(aftershootCredit)}
          />
        )}
      </dl>
      <UpgradePromoCode {...promo} isZeroAmount={amountDueToday === 0} />
      <dl className="flex items-start justify-between gap-3 border-t border-edge-card pt-4 text-type-1">
        <dt className="flex flex-col gap-0.5">
          <span className="text-base leading-[27.2px] font-semibold tracking-[0.16px]">
            {COPY.switchToday.total}
          </span>
          <span className="text-xs leading-[14.4px] tracking-[0.24px] italic">
            {COPY.switchToday.inclusiveOfTaxes}
          </span>
        </dt>
        <dd className="text-right text-xl leading-[44.2px] font-normal tracking-normal tabular-nums md:text-[34px]">
          {formatHeadlineAmount(amountDueToday, currency)}
        </dd>
      </dl>
    </div>
  );
};

export default UpgradeSummary;
